import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { navigate } from "gatsby";
import "./journey-shell.css";

const JourneyContext = createContext(null);
const STOPS = ["home", "films", "journal", "engineering", "contact", "outro"];
const ROUTES = {
    home: /^\/(?:ja\/)?(?:index\.html)?$/,
    films: /^\/(?:ja\/)?(?:portfolio|films)(?:\/|$)/,
    journal: /^\/(?:ja\/)?(?:blog|journal)(?:\/|$)/,
    engineering: /^\/(?:ja\/)?engineering(?:\/|$)/,
    contact: /^\/(?:ja\/)?contact(?:\/|$)/,
};
const labels = {
    en: {
        loading: "Preparing the studio",
        skip: "Skip intro",
        wordmark: "Sho, filmmaker and animation director",
        loadingWord: "Loading",
        videoUnavailable: "Intro video unavailable",
    },
    ja: {
        loading: "アトリエを準備しています",
        skip: "イントロをスキップ",
        wordmark: "映像作家・アニメーション監督 秋山翔",
        loadingWord: "読み込み中",
        videoUnavailable: "イントロ動画を再生できません",
    },
};

function unlocalizedPath(pathname = "/") {
    return pathname.replace(/^\/(?:en|ja)(?=\/|$)/, "") || "/";
}

export function routeFocus(pathname = "/") {
    const hash = pathname.split("#")[1];
    if (STOPS.includes(hash)) return hash;
    const path = unlocalizedPath(pathname.split(/[?#]/)[0]);
    for (const [focus, pattern] of Object.entries(ROUTES)) {
        if (pattern.test(path)) return focus;
    }
    return "home";
}

export function JourneyShell({ children }) {
    // Gatsby Intl keys page providers by route. Keep the world outside those
    // providers so a page change never recreates the renderer or its camera.
    const [pathname, setPathname] = useState("/");
    const [routeReady, setRouteReady] = useState(false);
    const focus = routeFocus(pathname);
    const locale =
        pathname.startsWith("/ja/") || pathname === "/ja" ? "ja" : "en";
    const article = /^\/(?:blog|journal)\/[^/]+/.test(
        unlocalizedPath(pathname)
    );
    const homeRoute =
        routeReady && /^\/(?:index\.html)?$/.test(unlocalizedPath(pathname));
    const [sceneProgress, setSceneProgress] = useState(null);
    const [World, setWorld] = useState(null);
    const [worldReady, setWorldReady] = useState(false);
    const [worldUnavailable, setWorldUnavailable] = useState(false);
    const [paused, setPaused] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);
    const [loading, setLoading] = useState(false);
    const [moving, setMoving] = useState(false);
    const [clientReady, setClientReady] = useState(false);
    const [introSeen, setIntroSeen] = useState(true);
    const [pendingPath, setPendingPath] = useState(null);
    const [showContent, setShowContent] = useState(true);
    const [introVisible, setIntroVisible] = useState(false);
    const navTimer = useRef(null);
    const introTimer = useRef(null);
    const videoRef = useRef(null);
    const [introMinElapsed, setIntroMinElapsed] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [assetProgress, setAssetProgress] = useState(0);
    const minIntroTimer = useRef(null);
    const lastPath = useRef(pathname);
    const returnFocus = useRef(null);
    const previousBodyOverflow = useRef(null);
    const skipButton = useRef(null);
    const contentRef = useRef(null);

    useEffect(() => {
        const updatePath = () => {
            setPathname(window.location.pathname);
            setRouteReady(true);
        };
        updatePath();
        window.addEventListener("studio-route-change", updatePath);
        setClientReady(true);
        let seen = false;
        try {
            seen = window.sessionStorage.getItem("studio-intro-seen") === "1";
        } catch (_) {
            // Storage can be unavailable in private browsing; this visit still works.
        }
        setIntroSeen(seen);
        const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReducedMotion(motion.matches);
        const updateMotion = () => setReducedMotion(motion.matches);
        motion.addEventListener?.("change", updateMotion);
        let active = true;
        import("./studio-world")
            .then((module) => {
                if (active) setWorld(() => module.default);
            })
            .catch(() => {
                if (active) setWorldUnavailable(true);
            });
        return () => {
            active = false;
            window.removeEventListener("studio-route-change", updatePath);
            motion.removeEventListener?.("change", updateMotion);
            window.clearTimeout(minIntroTimer.current);
            window.clearTimeout(navTimer.current);
            window.clearTimeout(introTimer.current);
        };
    }, []);

    useEffect(() => {
        if (
            !clientReady ||
            !homeRoute ||
            introSeen ||
            reducedMotion ||
            worldUnavailable
        )
            return undefined;
        setIntroVisible(true);
        setLoading(true);
        setVideoReady(false);
        setVideoError(false);
        setIntroMinElapsed(false);
        window.clearTimeout(minIntroTimer.current);
        minIntroTimer.current = window.setTimeout(
            () => setIntroMinElapsed(true),
            4800
        );
        introTimer.current = window.setTimeout(() => finishIntro(), 10000);
        return () => window.clearTimeout(introTimer.current);
        // finishIntro is intentionally stable via its ref-free state updates.
    }, [clientReady, homeRoute, introSeen, reducedMotion, worldUnavailable]);

    const finishIntro = useCallback(() => {
        window.clearTimeout(introTimer.current);
        window.clearTimeout(minIntroTimer.current);
        setIntroVisible(false);
        setLoading(false);
        setIntroSeen(true);
        setShowContent(true);
        try {
            window.sessionStorage.setItem("studio-intro-seen", "1");
        } catch (_) {
            // The loader still completes when session storage is unavailable.
        }
        if (videoRef.current) videoRef.current.pause();
    }, []);

    useEffect(() => {
        if (introVisible && introMinElapsed && worldReady && videoReady)
            finishIntro();
    }, [finishIntro, introMinElapsed, introVisible, videoReady, worldReady]);

    useEffect(() => {
        if (reducedMotion) {
            setLoading(false);
            setIntroVisible(false);
            setMoving(false);
            setShowContent(true);
        }
    }, [reducedMotion]);

    useEffect(() => {
        if (pathname === lastPath.current) return;
        lastPath.current = pathname;
        window.clearTimeout(navTimer.current);
        setPendingPath(null);
        setMoving(false);
        setShowContent(true);
    }, [pathname]);

    const arrive = useCallback(
        (arrivedFocus) => {
            if (!pendingPath) {
                setMoving(false);
                return;
            }
            if (arrivedFocus !== routeFocus(pendingPath)) return;
            const destination = pendingPath;
            window.clearTimeout(navTimer.current);
            setMoving(false);
            navigate(destination);
        },
        [pendingPath]
    );

    const navigateTo = useCallback(
        (path) => {
            if (!path || path.startsWith("http")) return false;
            const cleanPath = unlocalizedPath(path.split(/[?#]/)[0]);
            const isReader = /^\/blog\/[^/]+/.test(cleanPath);
            const isStop =
                /^\/(?:portfolio|films|blog|journal|engineering|contact)?\/?$/.test(
                    cleanPath
                );
            if (isStop) {
                const target = routeFocus(path);
                const destination = `/${locale}/#${target}`;
                window.clearTimeout(navTimer.current);
                setPendingPath(null);
                setMoving(false);
                setShowContent(true);
                if (document.querySelector(".scroll-journey")) {
                    window.history.replaceState(
                        window.history.state,
                        "",
                        destination
                    );
                    window.dispatchEvent(
                        new CustomEvent("studio-jump", { detail: { target } })
                    );
                } else {
                    navigate(destination);
                }
                return true;
            }
            if (isReader) {
                window.clearTimeout(navTimer.current);
                setPendingPath(null);
                setMoving(false);
                setShowContent(true);
                navigate(path);
                return true;
            }
            if (path === pathname) return false;
            if (routeFocus(path) === focus) {
                window.clearTimeout(navTimer.current);
                setPendingPath(null);
                setMoving(false);
                setShowContent(true);
                navigate(path);
                return true;
            }
            if (reducedMotion || paused || worldUnavailable || !World) {
                window.clearTimeout(navTimer.current);
                setPendingPath(null);
                setMoving(false);
                setShowContent(true);
                navigate(path);
                return true;
            }
            window.clearTimeout(navTimer.current);
            setPendingPath(path);
            setMoving(true);
            setShowContent(false);
            // Covers a lost callback, including WebGL context loss during a trip.
            navTimer.current = window.setTimeout(() => {
                setMoving(false);
                setShowContent(true);
                setPendingPath(null);
                navigate(path);
            }, 3400);
            return true;
        },
        [
            World,
            focus,
            pathname,
            locale,
            paused,
            reducedMotion,
            worldUnavailable,
        ]
    );

    const replayIntro = useCallback(() => {
        if (reducedMotion) return;
        try {
            window.sessionStorage.removeItem("studio-intro-seen");
        } catch (_) {}
        setIntroSeen(false);
        setIntroVisible(true);
        setLoading(true);
        setVideoReady(false);
        setVideoError(false);
        setIntroMinElapsed(false);
        window.clearTimeout(minIntroTimer.current);
        minIntroTimer.current = window.setTimeout(
            () => setIntroMinElapsed(true),
            4800
        );
        setShowContent(false);
        introTimer.current = window.setTimeout(() => finishIntro(), 10000);
    }, [finishIntro, reducedMotion]);

    const onWorldReady = useCallback(() => {
        setWorldReady(true);
        setAssetProgress(1);
        if (homeRoute && !introSeen && !reducedMotion) setLoading(true);
    }, [homeRoute, introSeen, reducedMotion]);
    const onWorldProgress = useCallback((fraction) => {
        if (Number.isFinite(fraction)) {
            setAssetProgress((previous) =>
                Math.max(previous, Math.max(0, Math.min(1, fraction)))
            );
        }
    }, []);
    const onWorldUnavailable = useCallback(() => {
        setWorldUnavailable(true);
        setWorldReady(false);
        window.clearTimeout(navTimer.current);
        if (pendingPath) navigate(pendingPath);
        setPendingPath(null);
        setMoving(false);
        setShowContent(true);
        finishIntro();
    }, [finishIntro, pendingPath]);
    const activeFocus =
        typeof sceneProgress === "number" && !article
            ? STOPS[
                  Math.round(
                      Math.max(0, Math.min(STOPS.length - 1, sceneProgress))
                  )
              ]
            : pendingPath
              ? routeFocus(pendingPath)
              : focus;
    const value = useMemo(
        () => ({
            navigateTo,
            sceneProgress,
            setSceneProgress,
            focus: activeFocus,
            moving,
            loading,
            paused,
            setPaused,
            reducedMotion,
            replayIntro,
        }),
        [
            navigateTo,
            sceneProgress,
            activeFocus,
            moving,
            loading,
            paused,
            reducedMotion,
            replayIntro,
        ]
    );
    const loadingModal = clientReady && loading;
    const text = labels[locale];

    useEffect(() => {
        const main = contentRef.current?.querySelector("main");
        if (!main) return;
        main.inert = !showContent;
        if (showContent) main.removeAttribute("aria-hidden");
        else main.setAttribute("aria-hidden", "true");
    }, [showContent]);

    useEffect(() => {
        if (!loadingModal) return undefined;
        returnFocus.current = document.activeElement;
        previousBodyOverflow.current = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        skipButton.current?.focus();
        return () => {
            document.body.style.overflow = previousBodyOverflow.current || "";
            if (returnFocus.current?.isConnected) returnFocus.current.focus();
        };
    }, [loadingModal]);
    return (
        <JourneyContext.Provider value={value}>
            <div
                className="journey-shell"
                data-focus={activeFocus}
                data-moving={moving ? "true" : "false"}
                data-loading={loadingModal ? "true" : "false"}
                data-article={article ? "true" : "false"}
                data-world-ready={worldReady ? "true" : "false"}
            >
                <div className="journey-world" aria-hidden="true">
                    {World && (
                        <World
                            focus={activeFocus}
                            sceneProgress={article ? null : sceneProgress}
                            entered={introSeen || reducedMotion || paused}
                            paused={paused || article}
                            reducedMotion={reducedMotion || paused || article}
                            onReady={onWorldReady}
                            onProgress={onWorldProgress}
                            onTravelEnd={arrive}
                            onUnavailable={onWorldUnavailable}
                        />
                    )}
                    <div className="journey-world-fallback" />
                </div>
                <div
                    ref={contentRef}
                    className="journey-content"
                    inert={loadingModal ? "" : undefined}
                    aria-hidden={loadingModal ? "true" : undefined}
                    data-visible={showContent ? "true" : "false"}
                >
                    {children}
                </div>
                {introVisible && loadingModal && (
                    <div
                        className="journey-loader"
                        role="dialog"
                        aria-modal="true"
                        aria-label={text.loading}
                        onKeyDown={(event) => {
                            if (event.key === "Escape") {
                                event.preventDefault();
                                finishIntro();
                                return;
                            }
                            if (event.key !== "Tab") return;
                            event.preventDefault();
                            skipButton.current?.focus();
                        }}
                    >
                        <video
                            ref={videoRef}
                            className="journey-loader__video"
                            src={
                                require("../../assets/home.mp4").default ||
                                require("../../assets/home.mp4")
                            }
                            poster="/studio/pencil-study.webp"
                            muted
                            autoPlay
                            playsInline
                            preload="metadata"
                            onCanPlay={() => setVideoReady(true)}
                            onEnded={() => {
                                setVideoReady(true);
                            }}
                            onError={() => {
                                setVideoError(true);
                                setVideoReady(true);
                            }}
                        />
                        <div
                            className="journey-loader__progress"
                            role="progressbar"
                            aria-label={text.loading}
                            aria-valuemin="0"
                            aria-valuemax="100"
                            aria-valuenow={Math.round(
                                (assetProgress + (videoReady ? 1 : 0)) * 50
                            )}
                        >
                            <div
                                className="journey-loader__progress-fill"
                                style={{
                                    width: `${Math.round((assetProgress + (videoReady ? 1 : 0)) * 50)}%`,
                                }}
                            />
                            <span>{text.loadingWord}</span>
                        </div>
                        {videoError && (
                            <p className="journey-loader__error" role="status">
                                {text.videoUnavailable}
                            </p>
                        )}
                        <div className="journey-loader__caption">
                            <button
                                ref={skipButton}
                                type="button"
                                onClick={finishIntro}
                                aria-label={text.skip}
                                className="journey-loader__skip"
                            >
                                {text.skip}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </JourneyContext.Provider>
    );
}

export function useStudioJourney() {
    return (
        useContext(JourneyContext) || {
            navigateTo: () => false,
            sceneProgress: null,
            setSceneProgress: () => {},
            focus: "home",
            moving: false,
            loading: false,
            paused: false,
            setPaused: () => {},
            reducedMotion: false,
            replayIntro: () => {},
        }
    );
}
